import { SDKStatusCode } from "@nebula-sdk/core";
import { defineComponent, onMounted, onUnmounted, reactive, ref, watch } from "vue";
import Button from "../../share/button";
import Checkbox from "../../share/checkbox";
import Input from "../../share/input";
import Link from "../../share/link";
import message from "../../share/message";
import Popup from "../../share/popup";
import AccountCaptcha from "./captcha";
import AccountFreeze from "./freeze";
import type { NebulaWEBGameSDKAccount } from "../../../account";
import type { SDKLoginResult } from "@nebula-sdk/core";
import type { PropType } from "vue";

import "./index.less";

export default defineComponent({
  props: {
    service: { type: Object as PropType<NebulaWEBGameSDKAccount>, required: true },
    success: { type: Function as PropType<(result: SDKLoginResult) => void>, required: true },
    error: { type: Function as PropType<(result: SDKLoginResult) => void>, required: true },
  },
  setup(props) {
    const visible = ref(false);
    const regex = /^1[3456789]\d{9}$/;
    const countdown = ref(0);
    const loading = ref(false);
    const model = reactive({ mobile: "", code: "", agree: false });

    const captchaVisible = ref(false);
    const freezeVisible = ref(false);

    const openUserContract = (event: MouseEvent) => {
      event.preventDefault();
      event.stopPropagation();
      void props.service.openUserContract();
    };

    const openPrivacyContract = (event: MouseEvent) => {
      event.preventDefault();
      event.stopPropagation();
      void props.service.openPrivacyContract();
    };

    const onCountdownChange = () => {
      props.service.onCountdownChange((seconds) => {
        countdown.value = seconds;
      });
    };

    const offCountdownChange = () => {
      props.service.offCountdownChange();
      countdown.value = 0;
    };

    const onCloseClick = (event: MouseEvent) => {
      event.preventDefault();
      event.stopPropagation();
      offCountdownChange();
      visible.value = false;
      props.error({
        status: "error",
        code: SDKStatusCode.LoginCancelError,
        msg: "登录失败",
        data: null,
      });
    };

    const onSendClick = async () => {
      const result = await props.service.sendVerifyCode({ mobile: model.mobile });
      if (result.status === "success") {
        onCountdownChange();
        model.code = "";
        return;
      }
      offCountdownChange();
      if (result.code === SDKStatusCode.SendVerifyLimitError) {
        captchaVisible.value = true;
      } else {
        message.info({ message: result.msg });
      }
    };

    const onMobileBlur = () => {
      if (!visible.value || regex.test(model.mobile)) return;
      message.info({ message: "请输入有效的手机号码" });
    };

    const onLoginClick = async () => {
      if (!model.mobile) {
        message.info({ message: "请输入手机号码" });
        return;
      }
      if (!regex.test(model.mobile)) {
        message.info({ message: "请输入有效的手机号码" });
        return;
      }
      if (!model.code) {
        message.info({ message: "请输入验证码" });
        return;
      }
      if (model.code.length !== 4) {
        message.info({ message: "请输入有效的验证码" });
        return;
      }
      loading.value = true;
      const result = await props.service.verifyInputCode({
        mobile: model.mobile,
        code: model.code,
      });
      loading.value = false;
      if (result.status === "error") {
        if (result.code === SDKStatusCode.LoginFreezeError) {
          freezeVisible.value = true;
        } else {
          message.info({ message: result.msg });
        }
        return;
      }
      visible.value = false;
      props.success({
        status: "success",
        code: SDKStatusCode.Success,
        msg: "登录成功",
        data: result.data,
      });
    };

    const onCaptchaConfirm = () => {
      void onSendClick();
      captchaVisible.value = false;
    };

    const onCaptchaCancel = () => {
      captchaVisible.value = false;
    };

    const onFreezeConfirm = () => {
      freezeVisible.value = false;
    };

    watch(
      () => model.mobile,
      () => {
        offCountdownChange();
        model.code = "";
      },
    );

    watch(
      () => visible.value,
      () => {
        if (!visible.value) return;
        model.mobile = "";
        model.code = "";
        model.agree = false;
        offCountdownChange();
      },
    );

    onMounted(() => {
      visible.value = true;
    });

    onUnmounted(() => {
      offCountdownChange();
    });

    return () => {
      return (
        <Popup position="center" v-model:visible={visible.value} width={320}>
          <div class="feiyu-web-service-login">
            <div class="feiyu-web-service-login--top">
              <div class="feiyu-web-service-login--top-logo" />
              <div class="feiyu-web-service-login--top-close" onMousedown={onCloseClick} />
            </div>
            <div class="feiyu-web-service-login--field">
              <Input
                maxLength={11}
                placeholder="请输入手机号码"
                type="tel"
                v-model={model.mobile}
                onBlur={onMobileBlur}
              />
            </div>
            <div class="feiyu-web-service-login--field">
              <Input maxLength={4} placeholder="请输入验证码" type="tel" v-model={model.code}>
                {{
                  suffix: () => (
                    <Link
                      disabled={!regex.test(model.mobile) || countdown.value > 0}
                      onClick={onSendClick}
                    >
                      {countdown.value > 0 ? `重新获取 ${countdown.value}S` : "获取验证码"}
                    </Link>
                  ),
                }}
              </Input>
            </div>
            <div class="feiyu-web-service-login--action">
              <Button
                disabled={!model.agree}
                loading={loading.value}
                type="primary"
                onClick={onLoginClick}
              >
                {"登录"}
              </Button>
            </div>
            <div class="feiyu-web-service-login--agree">
              <Checkbox v-model={model.agree}>
                <span>{"已阅读并同意"}</span>
                <span class="feiyu-web-service-login--agree-link" onClick={openUserContract}>
                  {"《用户协议》"}
                </span>
                <span>{"和"}</span>
                <span class="feiyu-web-service-login--agree-link" onClick={openPrivacyContract}>
                  {"《隐私条款》"}
                </span>
              </Checkbox>
            </div>
          </div>
          <AccountCaptcha
            mobile={model.mobile}
            service={props.service}
            visible={captchaVisible.value}
            onCancel={onCaptchaCancel}
            onConfirm={onCaptchaConfirm}
          />
          <AccountFreeze v-model:visible={freezeVisible.value} onConfirm={onFreezeConfirm} />
        </Popup>
      );
    };
  },
});
